import { withRouter } from 'next/router'
import CoachProfile from 'components/coach-profile'
import Layout from 'components/layout'
import contentfulClient from 'clients/contentful'
import { withApollo } from 'hoc/with-apollo'
import CurrentUserProvider from 'providers/current-user-provider'
import {
  CONTENTFUL_COACH_PROFILE_PAGE_ENTRY_ID,
  CONTENTFUL_HOMEPAGE_SIGNUP_SURVEY_ENTRY_ID,
} from 'components/coach-profile/constants'
import withCurrentUser from 'middleware/with-current-user'
import redirect from 'utilities/redirect'
import urls from 'utilities/urls'
import { getCoachByUsername } from './queries.graphql'
import QueryParamsContext from 'contexts/query-params-context'
import Error from 'next/error'
import bugsnagClient from 'clients/bugsnag'
import { HeeboProvider } from 'providers/font-providers/heebo-provider'
import { getExperimentKitSSR } from '../../utilities/experiment/server'
import { useExperimentKit } from '../../utilities/experiment/context'
import PROJECTS from '../../utilities/experiment/constants'
import { COACH_LEVELS } from '../../components/coach-level-badge/constants'

const CoachProfilePage = ({
  router,
  coachProfilePageEntry,
  surveyEntry,
  user,
  experimentKitData,
  statusCode,
  referrer,
}) => {
  useExperimentKit(experimentKitData)

  if (statusCode !== 200) {
    return <Error statusCode={statusCode} />
  }
  // next does not allow different dynamic urls
  // so even though page name is id, it expects a username
  const {
    query: { id: username, ...rest },
    pathname,
  } = router

  const { title, metaTags, noIndex, canonicalUrl } =
    coachProfilePageEntry.fields || {}

  return (
    <QueryParamsContext.Provider value={rest}>
      <HeeboProvider>
        <Layout
          title={title}
          metaTags={metaTags}
          noIndex={pathname.endsWith('/free') || noIndex}
          canonicalUrl={canonicalUrl}
          referrer={referrer}>
          <CurrentUserProvider>
            <CoachProfile
              username={username}
              user={user}
              coachProfilePageEntry={coachProfilePageEntry}
              surveyEntry={surveyEntry}
              query={router.query}
            />
          </CurrentUserProvider>
        </Layout>
      </HeeboProvider>
    </QueryParamsContext.Provider>
  )
}

CoachProfilePage.getInitialProps = withCurrentUser(
  async ({
    apolloClient,
    ctx,
    ctx: {
      query,
      query: { id, ref, ...rest },
      req,
      res,
    },
    currentUserPayload: {
      data: { me },
    },
  }) => {
    res &&
      res.setHeader(
        'cache-control',
        `s-maxage=${60 * 60}, stale-while-revalidate`
      )

    let referrer
    if (req) {
      referrer = req.headers.referer
    }

    // redirect away from free trial page
    if (ref === 'gp_freetrial_coach') {
      return redirect(urls.coachReferral({ username: id, ...rest }), ctx)
    }

    /**
     * Query used to pull back the info on if the
     * coach associated with the CLP is active
     */
    let coachPayload
    try {
      coachPayload = await apolloClient.query({
        query: getCoachByUsername,
        variables: {
          username: id,
        },
      })
    } catch (e) {
      console.error(e)
      bugsnagClient.notify(e)
      return {
        statusCode: 404,
      }
    }

    /**
     * If the coach is not active or in review, redirect to the Kickoff home page
     */
    if (
      !coachPayload?.data?.coachByUsername?.isActive ||
      coachPayload?.data?.coachByUsername?.level?.id ===
        COACH_LEVELS.AWAITING_REVIEW
    ) {
      return redirect(urls.homePage()?.href, ctx)
    }

    const { ek, data } = await getExperimentKitSSR(ctx, {
      url: ctx.asPath,
      attributes: { projectId: PROJECTS.WEB, url: ctx.asPath },
      user: me?.user,
      devMode: process.env.NODE_ENV === 'development',
    })

    const clp = ek.experiment('clp')

    const profilePageEntryId = clp?.experimentResult?.landingPage?.sys?.id

    const coachProfilePageEntry = await contentfulClient.getEntry(
      profilePageEntryId || CONTENTFUL_COACH_PROFILE_PAGE_ENTRY_ID,
      {
        include: 10,
      }
    )
    const surveyEntry = await contentfulClient.getEntry(
      CONTENTFUL_HOMEPAGE_SIGNUP_SURVEY_ENTRY_ID,
      {
        include: 10,
      }
    )

    return {
      statusCode: 200,
      coachProfilePageEntry,
      surveyEntry,
      query,
      user: me.user,
      experimentKitData: data,
      referrer,
    }
  }
)

export default withRouter(withApollo(CoachProfilePage))
